'use client';

import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import { ProductInnerZigzagItem, ImageType } from '@/types/product';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';

function ZigzagItemCopy({ item }: { item: ProductInnerZigzagItem }) {
  const itemHeading = item.heading?.trim();
  const titleLines = itemHeading ? getTitleLines(itemHeading) : [];

  return (
    <>
      {itemHeading ? (
        <h3 className="mb-6 text-3xl font-semibold tracking-tight text-[#edebe4] md:text-4xl lg:text-[2.5rem] lg:leading-tight">
          {titleLines.map((line) => (
            <span key={line} className="block overflow-hidden">
              <span data-split-title-line className="block">
                {line}
              </span>
            </span>
          ))}
        </h3>
      ) : null}
      <div
        data-split-copy
        className="prose prose-sm prose-invert mb-0 max-w-xl text-sm font-light leading-[1.85] text-[#edebe4]/45 md:text-base"
        dangerouslySetInnerHTML={{ __html: item.description || '' }}
      />
    </>
  );
}

function ZigzagMedia({ media, alt }: { media: ImageType; alt: string }) {
  const src = resolveStrapiMediaUrl(media?.url) || MEDIA_PLACEHOLDER;

  return (
    <CmsImage
      src={src}
      media={media}
      alt={alt}
      fill
      sizes="(max-width: 1023px) 100vw, 50vw"
      className="object-cover"
      posterSrc={MEDIA_PLACEHOLDER}
      fallbackSrc={MEDIA_PLACEHOLDER}
    />
  );
}

interface ProductSustainabilitySplitProps {
  items?: ProductInnerZigzagItem[];
  /** Optional section header from the CMS. When omitted (or empty), the
   *  header block is not rendered at all — avoids OPC-specific copy from
   *  appearing on PPC where the zigzag section ships items without a header. */
  heading?: string | null;
  /** Optional CMS subheading (HTML allowed). */
  subHeading?: string | null;
}

const defaultItems: ProductInnerZigzagItem[] = [
  {
    id: 1,
    media: {
      id: 1,
      url: '/images/sustain.png',
      alternativeText: 'Sustainability plant',
    },
    description:
      "By harnessing one of India's largest Waste Heat Recovery Systems (60 MW) and maintaining a Zero Liquid Discharge footprint, we ensure every bag of cement is a step toward a water-positive and carbon-conscious future.",
  },
  {
    id: 2,
    media: {
      id: 2,
      url: '/images/sustain.png',
      alternativeText: 'Sustainability plant',
    },
    description:
      "By harnessing one of India's largest Waste Heat Recovery Systems (60 MW) and maintaining a Zero Liquid Discharge footprint, we ensure every bag of cement is a step toward a water-positive and carbon-conscious future.",
  },
];

function getTitleLines(title: string) {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return words;
  if (words.length <= 5) return [words.slice(0, 2).join(' '), words.slice(2).join(' ')];
  return [words.slice(0, 2).join(' '), words.slice(2, 5).join(' '), words.slice(5).join(' ')];
}

export default function ProductSustainabilitySplit({
  items = defaultItems,
  heading,
  subHeading,
}: ProductSustainabilitySplitProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const trimmedHeading = heading?.trim();
  const trimmedSubHeading = subHeading?.trim();
  const showHeader = Boolean(trimmedHeading || trimmedSubHeading);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      gsap.utils.toArray<HTMLElement>('[data-split-panel]').forEach((panel) => {
        const media = panel.querySelector<HTMLElement>('[data-split-media]');
        const image = panel.querySelector<HTMLElement>('[data-split-image]');
        const eyebrow = panel.querySelector<HTMLElement>('[data-split-eyebrow]');
        const titleLines = gsap.utils.toArray<HTMLElement>(
          panel.querySelectorAll('[data-split-title-line]'),
        );
        const copy = panel.querySelector<HTMLElement>('[data-split-copy]');
        const isReversed = panel.dataset.splitReverse === 'true';

        gsap.set(media, {
          clipPath: isReversed ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)',
        });
        gsap.set(titleLines, { yPercent: 105 });
        gsap.set([eyebrow, copy].filter(Boolean), { autoAlpha: 0, y: 24 });

        gsap
          .timeline({
            scrollTrigger: {
              trigger: panel,
              start: 'top 78%',
              once: true,
            },
          })
          .to(media, {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1.2,
            ease: 'expo.inOut',
          })
          .to(
            titleLines,
            {
              yPercent: 0,
              duration: 0.9,
              stagger: 0.09,
              ease: 'expo.out',
            },
            '<0.25',
          )
          .to(
            [eyebrow, copy].filter(Boolean),
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.7,
              stagger: 0.1,
              ease: 'power2.out',
            },
            '<0.15',
          );

        if (image) {
          gsap.to(image, {
            y: -60,
            ease: 'none',
            scrollTrigger: {
              trigger: panel,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.5,
            },
          });
        }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-page-reveal="false"
      className="bg-[#000000] text-[#edebe4] max-sm:-mt-[1px]"
    >
      {showHeader && (
        <div className="container mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-0 mb-10">
          {trimmedHeading && (
            <h2 className="text-3xl font-semibold tracking-tight text-[#edebe4] md:text-4xl lg:text-[2.5rem] lg:leading-tight">
              {trimmedHeading}
            </h2>
          )}
          {trimmedSubHeading && (
            <div
              className="mx-auto mt-5 max-w-2xl text-base font-light leading-relaxed text-[#edebe4]/45 md:text-lg [&_p]:m-0"
              dangerouslySetInnerHTML={{ __html: trimmedSubHeading }}
            />
          )}
        </div>
      )}

      {items.map((item, index) => (
        <div
          key={item.id}
          data-split-panel
          data-split-reverse={index % 2 !== 0}
          className="grid min-h-[720px] grid-cols-1 border-t border-[#edebe4]/10 lg:min-h-screen lg:grid-cols-2"
        >
          <div
            data-split-media
            className={`relative min-h-[360px] overflow-hidden lg:min-h-0 ${
              index % 2 !== 0 ? 'lg:order-2' : ''
            }`}
          >
            <div data-split-image className="absolute inset-0 h-[112%]">
              <ZigzagMedia
                media={item.media}
                alt={item.media?.alternativeText || 'Product media'}
              />
            </div>
            <div
              aria-hidden
              className={`absolute inset-0 ${
                index % 2 !== 0
                  ? 'bg-[linear-gradient(to_left,transparent_55%,#0c0c0b_100%)]'
                  : 'bg-[linear-gradient(to_right,transparent_55%,#0c0c0b_100%)]'
              }`}
            />
          </div>

          <div className="flex flex-col justify-center px-4 py-10 sm:px-8 sm:py-12 md:py-14 lg:px-20">
            <div
              data-split-eyebrow
              className="mb-7 flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.34em] text-red-600"
            >
              {index % 2 === 0 ? 'Engineered Endurance' : 'Future Ready Build'}
            </div>
            <ZigzagItemCopy item={item} />
          </div>
        </div>
      ))}
    </section>
  );
}
